import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import Button from '../components/common/Button'
import Card from '../components/common/Card'
import Spinner from '../components/common/Spinner'
import i18n from '../i18n'
import { daysAgo } from '../lib/archive'
import { CHANNELS, KIND_FILTERS, KIND_TILE, TARGET_CTA, TARGET_ROUTE, filterByKind, markReadLocally, unreadCountOf } from '../lib/notifications'
import { faDigits } from '../lib/numerals'
import { classifyFailure } from '../services/api'
import { listNotifications, markNotificationsRead, notificationPreferences, updateNotificationPreferences } from '../services/notifications'

/** A plain function, not a component, so it reads `i18n.t()` directly rather than the `useTranslation()` hook. */
function whenLabel(isoInstant) {
  const days = daysAgo(isoInstant)
  if (days === null) return ''
  if (days === 0) return i18n.t('whenLabel.today', { ns: 'notifications' })
  if (days === 1) return i18n.t('whenLabel.yesterday', { ns: 'notifications' })
  return i18n.t('whenLabel.daysAgo', { ns: 'notifications', count: faDigits(days) })
}

const KIND_ICON = {
  result: <path d="M9 12l2 2 4-4M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" />,
  streak: <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-1.5.5-2.5 1-3 .5 1.5 1.5 2 2 2 0-3-1-5 1-8z" />,
  pay: <path d="M3 7h18v10H3zM3 11h18M7 15h3" />,
  social: <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />,
}

function Row({ item, onOpen }) {
  const { t } = useTranslation('notifications')
  const unread = !item.readAt
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={`flex w-full items-start gap-3 rounded-2xl bg-surface p-3.5 text-start transition ${
          unread ? 'border-[1.5px] border-brand-600 shadow-sm' : 'border border-line'
        }`}
      >
        <span className={`grid size-[38px] shrink-0 place-items-center rounded-[11px] ${KIND_TILE[item.kind] ?? KIND_TILE.result}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {KIND_ICON[item.kind] ?? KIND_ICON.result}
          </svg>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="flex items-center gap-2">
            <span className="flex-1 text-[13.5px] font-bold text-ink">{item.title}</span>
            {unread && <span aria-label={t('row.unreadAria')} className="size-[7px] shrink-0 rounded-full bg-brand-600" />}
          </span>
          <span className="text-xs leading-[1.85] text-ink-2">{item.body}</span>
          <span className="flex items-center gap-2 pt-0.5 text-[10.5px]">
            <span className="text-ink-3">{whenLabel(item.createdAt)}</span>
            <span className="font-bold text-brand-700">{TARGET_CTA[item.target] ?? TARGET_CTA.home}</span>
          </span>
        </span>
      </button>
    </li>
  )
}

function Toggle({ label, note, checked, onToggle, last }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onToggle}
      className={`flex w-full items-center gap-3 py-3.5 text-start ${last ? '' : 'border-b border-line'}`}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[13.5px] font-semibold text-ink">{label}</span>
        <span className="text-[11px] text-ink-3">{note}</span>
      </span>
      <span className={`flex h-[29px] w-12 shrink-0 items-center rounded-full p-[3px] transition-colors ${checked ? 'bg-brand-600' : 'bg-track'}`}>
        <span className={`size-[23px] rounded-full bg-white shadow transition-transform ${checked ? '-translate-x-[19px]' : 'translate-x-0'}`} />
      </span>
    </button>
  )
}

/**
 * Notifications (design page 15, `notifs`; P-notifs of the go-live plan):
 * the server's inbox (B4), its read state, and the five channel switches.
 *
 * A tap marks the row read — locally at once, on the server right after —
 * and lands on what it is about. The switches gate what the server
 * pushes; rows are written regardless, so switching a channel off never
 * hides history. Until the list answers the page shows a spinner, never
 * the empty state.
 */
export default function Notifications() {
  const { t } = useTranslation('notifications')
  const navigate = useNavigate()
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState(null)
  const [prefs, setPrefs] = useState(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    listNotifications()
      .then((data) => {
        if (!cancelled) setItems(data.items)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    notificationPreferences()
      .then((data) => {
        if (!cancelled) setPrefs(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reload])

  const unread = unreadCountOf(items ?? [])
  const channels = CHANNELS()

  function open(item) {
    setItems((current) => markReadLocally(current ?? [], { ids: [item.id] }))
    if (!item.readAt) markNotificationsRead({ ids: [item.id] }).catch(() => {})
    navigate(TARGET_ROUTE[item.target] ?? TARGET_ROUTE.home)
  }

  function markAll() {
    if (unread === 0) return
    setItems((current) => markReadLocally(current ?? [], { all: true }))
    markNotificationsRead({ all: true }).catch(() => {})
  }

  function toggle(key) {
    if (!prefs) return
    const next = { ...prefs, [key]: !prefs[key] }
    setPrefs(next)
    updateNotificationPreferences({ [key]: next[key] })
      .then((saved) => setPrefs(saved))
      .catch(() => setPrefs(prefs))
  }

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <Link to="/profile" aria-label={t('page.backToProfile')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
        <button
          type="button"
          onClick={markAll}
          disabled={unread === 0}
          className="h-9 rounded-full border border-line bg-surface px-3 text-[11.5px] font-semibold text-ink-2 disabled:opacity-70"
        >
          {unread > 0 ? t('page.markAllButton') : t('page.allReadButton')}
        </button>
      </header>

      {error && !items ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('page.error.offline') : t('page.error.other')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('page.retry')}
          </Button>
        </div>
      ) : !items ? (
        <Spinner label={t('page.loading')} />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 px-2.5 py-8 text-center">
          <span className="grid size-[84px] place-items-center rounded-full bg-surface-2">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="text-ink-3" aria-hidden="true">
              <path d="M18 15V10a6 6 0 0 0-12 0v5l-2 3h16zM10 21h4" />
            </svg>
          </span>
          <p className="text-[17px] font-bold text-ink">{t('page.empty.title')}</p>
          <p className="max-w-[250px] text-[12.5px] leading-[1.9] text-ink-2">{t('page.empty.body')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {KIND_FILTERS().map((option) => (
              <button
                key={option.label}
                type="button"
                aria-pressed={filter === option.key}
                onClick={() => setFilter(option.key)}
                className={`h-[34px] rounded-full px-3.5 text-[11.5px] font-semibold transition-colors ${
                  filter === option.key ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface text-ink-2 ring-1 ring-line'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {filterByKind(items, filter).length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-2">{t('page.emptyFiltered')}</p>
          ) : (
            <ul className="space-y-2">
              {filterByKind(items, filter).map((item) => (
                <Row key={item.id} item={item} onOpen={open} />
              ))}
            </ul>
          )}
        </div>
      )}

      <section className="space-y-2.5 pt-0.5">
        <h2 className="text-xs font-semibold text-ink-3">{t('page.channelsHeading')}</h2>
        <Card as="div" pad="px-4 py-1.5">
          {channels.map((channel, index) => (
            <Toggle
              key={channel.key}
              label={channel.label}
              note={channel.note}
              checked={Boolean(prefs?.[channel.key])}
              onToggle={() => toggle(channel.key)}
              last={index === channels.length - 1}
            />
          ))}
        </Card>
        <p className="text-[11px] leading-[1.9] text-ink-3">
          {t('page.channelsNote')}
        </p>
      </section>
    </div>
  )
}
